import type { JiraIssue } from '../types'

// Pure helpers for the Jira (Commercial AEM BAU) operational view: filtering,
// aggregation for the request-type pie and product/label counts, and the
// period slicing that feeds the roll-up tiles. No React, no side effects -
// all unit-tested in jira.test.ts.

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Request-type colours: the three the stakeholder tracks, plus a palette for
// anything else Jira returns (Epic, Sub-task, Incident, …). The fallbacks
// deliberately avoid blue/cyan/red so an extra type can't be mistaken for
// Story/Task/Bug.
export const TYPE_COLORS: Record<string, string> = {
  Story: '#005EB8', // blue
  Task: '#00ACED', // cyan
  Bug: '#EF3340', // red
}
const TYPE_FALLBACK_COLORS = ['#8B5CF6', '#F88806', '#54AD18', '#EC4899', '#0D9488', '#A16207', '#94a3b8']

export function colorForType(type: string, index: number): string {
  return TYPE_COLORS[type] ?? TYPE_FALLBACK_COLORS[index % TYPE_FALLBACK_COLORS.length]
}

// Assign a colour to each type: the three fixed ones keep their colour; every
// OTHER type gets the next distinct fallback (indexed by how many unknown types
// came before it, not its overall position - otherwise a Sub-task sitting at
// index 3 could reuse a colour meant for a fixed type's slot).
export function buildTypeColors(types: string[]): Map<string, string> {
  const m = new Map<string, string>()
  let fallback = 0
  for (const t of types) {
    m.set(t, colorForType(t, fallback))
    if (!(t in TYPE_COLORS)) fallback += 1
  }
  return m
}

export interface JiraFilter {
  years: string[] // 'YYYY' values; empty = all
  months: string[] // 'MM' values (01-12); empty = all
  statuses: string[]
  types: string[]
  labels: string[]
  priorities: string[]
}

export const EMPTY_JIRA_FILTER: JiraFilter = {
  years: [],
  months: [],
  statuses: [],
  types: [],
  labels: [],
  priorities: [],
}

// [value, label] pairs for the month multi-select ('01' -> 'Jan', …).
export const MONTH_OPTIONS: [string, string][] = MONTH_NAMES.map((n, i) => [
  String(i + 1).padStart(2, '0'),
  n,
])

// 'YYYY-MM-DD' -> 'YYYY-MM' (null-safe).
export function monthKeyOf(date: string | null): string | null {
  return date && date.length >= 7 ? date.slice(0, 7) : null
}

// 'YYYY-MM' -> 'Jun 25'
export function fmtMonthKey(m: string): string {
  const [y, mo] = m.split('-')
  return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y.slice(2)}`
}

// Previous calendar month as { years, months } - the default Jira view (a
// single year + month preselected), matching how TrackIT shows "last month".
export function prevMonthParts(now: Date = new Date()): { years: string[]; months: string[] } {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { years: [String(d.getFullYear())], months: [String(d.getMonth() + 1).padStart(2, '0')] }
}

// Filter by created date (years/months) + the multi-value facets. Every facet
// is OR-within, AND-across. An issue with no priority is excluded once a
// priority filter is active (it matches none).
export function filterJiraIssues(issues: JiraIssue[], f: JiraFilter): JiraIssue[] {
  return issues.filter(i => {
    const c = i.created
    if (f.years.length && (!c || !f.years.includes(c.slice(0, 4)))) return false
    if (f.months.length && (!c || !f.months.includes(c.slice(5, 7)))) return false
    if (f.statuses.length && !f.statuses.includes(i.status)) return false
    if (f.types.length && !f.types.includes(i.type)) return false
    if (f.priorities.length && !f.priorities.includes(i.priority ?? '')) return false
    if (f.labels.length && !i.labels.some(l => f.labels.includes(l))) return false
    return true
  })
}

// Normalise a Jira priority name to P1-P5 (P0 folds into P1, the most urgent),
// so headline counts work across schemes: "P0".."P4", or word-based
// (Blocker/Highest/Critical/High/Major/Medium/Low/Minor/Lowest/Trivial).
export function normalisePriority(name: string | null): string | null {
  if (!name) return null
  const s = name.toLowerCase()
  const digit = s.match(/p?\s*([0-5])\b/)
  if (digit) return digit[1] === '0' ? 'P1' : `P${digit[1]}`
  if (/(blocker|highest|critical)/.test(s)) return 'P1'
  if (/(high|major)/.test(s)) return 'P2'
  if (/(medium|moderate|normal)/.test(s)) return 'P3'
  if (/(lowest|trivial)/.test(s)) return 'P5'
  if (/(minor|low)/.test(s)) return 'P4'
  return null
}

// Request-type counts for the pie, biggest first.
export function countByType(issues: JiraIssue[]): { type: string; count: number }[] {
  const m = new Map<string, number>()
  for (const i of issues) m.set(i.type, (m.get(i.type) ?? 0) + 1)
  return [...m.entries()].map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count)
}

// Work state across issues, from Jira's coarse statusCategory bucket:
//   new -> To Do, indeterminate -> In Progress, done -> Done.
export function statusBreakdown(issues: JiraIssue[]): { todo: number; inProgress: number; done: number } {
  let todo = 0
  let inProgress = 0
  let done = 0
  for (const i of issues) {
    if (i.statusCategory === 'done') done += 1
    else if (i.statusCategory === 'indeterminate') inProgress += 1
    else todo += 1 // 'new' or anything unexpected
  }
  return { todo, inProgress, done }
}

// Count by label/product - one issue with 5 labels contributes to all 5.
export function countByLabel(issues: JiraIssue[]): { label: string; count: number }[] {
  const m = new Map<string, number>()
  for (const i of issues) for (const l of i.labels) m.set(l, (m.get(l) ?? 0) + 1)
  return [...m.entries()].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count)
}

// Distinct created-month keys, ascending.
export function jiraMonths(issues: JiraIssue[]): string[] {
  const s = new Set<string>()
  for (const i of issues) {
    const mk = monthKeyOf(i.created)
    if (mk) s.add(mk)
  }
  return [...s].sort()
}

// Slice a sorted-ascending month list by the page-level period control, using
// the same semantics as the ServiceNow view so the roll-up tiles line up.
export function sliceMonthsByPeriod(months: string[], period: string, now: Date = new Date()): string[] {
  if (!months.length) return []
  switch (period) {
    case 'MTD':
      return months.slice(-1)
    case '3M':
      return months.slice(-3)
    case '6M':
      return months.slice(-6)
    case '12M':
      return months.slice(-12)
    case 'YTD': {
      const y = now.getFullYear()
      return months.filter(m => m >= `${y}-01`)
    }
    default:
      return months.slice(-3)
  }
}

// Distinct years present in the data, newest first - for the year dropdown.
export function availableYears(issues: JiraIssue[]): string[] {
  const s = new Set<string>()
  for (const i of issues) if (i.created) s.add(i.created.slice(0, 4))
  return [...s].sort().reverse()
}
